import { Injectable, inject } from '@angular/core';
import { Firestore, collection, getDocs } from '@angular/fire/firestore';
import { Song } from '../entities/song.model';
import { Playlist } from '../entities/playlist.model';
import { UserLookupService } from './user-lookup.service';

export interface LeaderboardEntry {
  uid: string;
  displayName: string;
  score: number;
  songLikes: number;
  songsInOthersPlaylists: number;
  playlistsWithLikes: number;
  rank: number;
}

const SONG_LIKE_POINTS = 3;
const SONG_CONTRIBUTED_POINTS = 2;
const PLAYLIST_POINTS = 1;

const EXCLUDED_UIDS = new Set(['b1yknz3FL0SXpuZsKiXnWDTgIfQ2']);

@Injectable({ providedIn: 'root' })
export class LeaderboardService {
  private firestore = inject(Firestore);
  private userLookup = inject(UserLookupService);

  async getLeaderboard(): Promise<LeaderboardEntry[]> {
    const [songsSnap, playlistsSnap] = await Promise.all([
      getDocs(collection(this.firestore, 'songs')),
      getDocs(collection(this.firestore, 'playlists'))
    ]);

    const songs = songsSnap.docs.map(d => ({ id: d.id, ...d.data() } as Song));
    const playlists = playlistsSnap.docs.map(d => ({ id: d.id, ...d.data() } as Playlist));

    // Build map: playlistId -> createdByUid
    const playlistOwner = new Map<string, string>();
    for (const p of playlists) {
      if (p.id && p.createdByUid) {
        playlistOwner.set(p.id, p.createdByUid);
      }
    }

    // Collect all UIDs
    const uids = new Set<string>();
    for (const s of songs) {
      if (s.submittedByUid) uids.add(s.submittedByUid);
    }
    for (const p of playlists) {
      if (p.createdByUid) uids.add(p.createdByUid);
    }

    type UserStat = { songLikes: number; songsInOthers: number; playlists: number };
    const stats = new Map<string, UserStat>();
    for (const uid of uids) {
      stats.set(uid, { songLikes: 0, songsInOthers: 0, playlists: 0 });
    }

    for (const song of songs) {
      if (!song.submittedByUid) continue;
      const s = stats.get(song.submittedByUid)!;
      s.songLikes += song.upvotes || 0;

      const playlistCreatorUid = playlistOwner.get(song.topic);
      if (playlistCreatorUid && playlistCreatorUid !== song.submittedByUid) {
        s.songsInOthers++;
      }
    }

    for (const playlist of playlists) {
      if (!playlist.createdByUid) continue;
      const s = stats.get(playlist.createdByUid)!;
      if ((playlist.totalLikes ?? 0) >= 1) {
        s.playlists++;
      }
    }

    const entries: LeaderboardEntry[] = [];
    for (const [uid, stat] of stats.entries()) {
      if (EXCLUDED_UIDS.has(uid)) continue;
      const score =
        stat.songLikes * SONG_LIKE_POINTS +
        stat.songsInOthers * SONG_CONTRIBUTED_POINTS +
        stat.playlists * PLAYLIST_POINTS;

      if (score > 0) {
        entries.push({
          uid,
          displayName: '',
          score,
          songLikes: stat.songLikes,
          songsInOthersPlaylists: stat.songsInOthers,
          playlistsWithLikes: stat.playlists,
          rank: 0
        });
      }
    }

    entries.sort((a, b) => b.score - a.score);
    entries.forEach((e, i) => (e.rank = i + 1));

    // Resolve display names
    const allUids = entries.map(e => e.uid);
    if (allUids.length) {
      const names = await this.userLookup.resolveMany(allUids);
      for (const entry of entries) {
        entry.displayName = names[entry.uid] || entry.uid;
      }
    }

    return entries;
  }
}
